import PropTypes from "prop-types";
import React, { forwardRef } from "react";
import { Button, ButtonToolbar, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { FormattedMessage, useIntl } from "react-intl";

import genericTranslations from "../../../utils/genericTranslations.js";
import { getDateLocale } from "../../../utils/utils.js";

const messages = {
  notificationText: {
    id: "modules.UserNotifications.components.NotificationMessageForm.notificationText",
    description: "Message content",
    defaultMessage: "Message",
  },
  startDate: {
    id: "modules.UserNotifications.components.NotificationMessageForm.startDate",
    description: "Message start date",
    defaultMessage: "Start Date",
  },
  endDate: {
    id: "modules.UserNotifications.components.NotificationMessageForm.endDate",
    description: "Message end date",
    defaultMessage: "End Date",
  },
  timeCaption: {
    id: "modules.ArriVideo.components.NotificationMessageForm.timeCaption",
    description: "Time caption label.",
    defaultMessage: "Time",
  },
  todayButton: {
    id: "modules.ArriVideo.components.NotificationMessageForm.todayButton",
    description: "Today button label.",
    defaultMessage: "Today",
  },
};

// Custom input for DatePicker that display a Bootstrap Form.Control
// used to be able to highlight the input validation errors
const CustomDateInput = forwardRef(
  ({ value, onClick, onChange, className, placeholder }, ref) => (
    <Form.Control
      type="text"
      value={value}
      onClick={onClick}
      onChange={onChange}
      ref={ref}
      className={className}
      placeholder={placeholder}
    />
  ),
);
CustomDateInput.displayName = "CustomDateInput";

const UserNotificationForm = ({
  handleChange,
  handleSubmit,
  errors,
  isSubmitting,
  onClickDelete,
  setFieldValue,
  values,
}) => {
  const intl = useIntl();
  const dateLocale = getDateLocale(intl.locale);

  const startDate = values.startDate ? new Date(values.startDate) : null;
  const endDate = values.endDate ? new Date(values.endDate) : null;

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="notificationText" className="mb-3">
        <Form.Label>
          <FormattedMessage {...messages.notificationText} />
        </Form.Label>
        <Form.Control
          type="text"
          autoComplete="off"
          value={values.notificationText || ""}
          onChange={handleChange}
          isInvalid={!values.notificationText}
        />
        <Form.Control.Feedback type="invalid">
          {errors.notificationText}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="startDate" className="mb-3">
        <Form.Label>
          <FormattedMessage {...messages.startDate} />
        </Form.Label>
        <br />
        <DatePicker
          className="form-control"
          selected={startDate}
          onChange={(date) => setFieldValue("startDate", date)}
          showTimeSelect
          dateFormat="MMMM d, yyyy HH:mm"
          timeFormat="HH:mm"
          timeCaption={intl.formatMessage(messages.timeCaption)}
          timeIntervals={15}
          todayButton={intl.formatMessage(messages.todayButton)}
          locale={dateLocale}
          minDate={new Date()}
          customInput={
            <CustomDateInput
              className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
            />
          }
        />
        <Form.Control.Feedback type="invalid" className="d-block">
          {errors.startDate}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="endDate" className="mb-3">
        <Form.Label>
          <FormattedMessage {...messages.endDate} />
        </Form.Label>
        <br />
        <DatePicker
          className="form-control"
          selected={endDate}
          onChange={(date) => setFieldValue("endDate", date)}
          showTimeSelect
          dateFormat="MMMM d, yyyy HH:mm"
          timeFormat="HH:mm"
          timeCaption={intl.formatMessage(messages.timeCaption)}
          timeIntervals={15}
          todayButton={intl.formatMessage(messages.todayButton)}
          locale={dateLocale}
          minDate={startDate}
          customInput={
            <CustomDateInput
              className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
            />
          }
        />
        <Form.Control.Feedback type="invalid" className="d-block">
          {errors.endDate}
        </Form.Control.Feedback>
      </Form.Group>

      <hr />

      <Form.Group controlId="formActions" className="mb-3">
        <ButtonToolbar>
          {values.id && (
            <Button variant="danger" className="me-2" onClick={onClickDelete}>
              <FormattedMessage {...genericTranslations.delete} />
            </Button>
          )}
          <Button
            type="submit"
            variant="light"
            disabled={isSubmitting || Object.keys(errors).length > 0}
          >
            <FormattedMessage {...genericTranslations.formSubmitLabel} />
          </Button>
        </ButtonToolbar>
      </Form.Group>
    </Form>
  );
};

UserNotificationForm.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onClickDelete: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    notificationText: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  values: PropTypes.shape({
    id: PropTypes.string,
    notificationText: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
};

export default UserNotificationForm;
